// Migration utilities for handling Firestore document schema upgrades

export interface MigrationContext {
    documentId: string;
    collectionName: string;
    currentVersion: number;
    targetVersion: number;
}

export interface MigrationResult<T> {
    data: T;
    version: number;
    migrated: boolean;
}

export type MigrationFunction<T> = (data: any, context: MigrationContext) => T | Promise<T>;

export interface MigrationDefinition<T> {
    version: number;
    migrate: MigrationFunction<T>;
    description: string;
}

/**
 * Base interface for all models that support migrations
 */
export interface MigratableModel {
    /** Schema version of this document */
    schemaVersion?: number;
}

/**
 * Migration manager for handling document schema upgrades
 */
export class MigrationManager<T extends MigratableModel> {
    private migrations: MigrationDefinition<T>[] = [];
    private currentVersion: number;

    constructor(currentVersion: number) {
        this.currentVersion = currentVersion;
    }

    /**
     * Register a migration function for a specific version
     */
    registerMigration(definition: MigrationDefinition<T>): void {
        this.migrations.push(definition);
        // Sort migrations by version to ensure proper order
        this.migrations.sort((a, b) => a.version - b.version);
    }

    /**
     * Migrate a document from its current version to the target version
     */
    async migrateDocument(
        data: any,
        documentId: string,
        collectionName: string
    ): Promise<MigrationResult<T>> {
        return this.migrateDocumentToVersion(data, documentId, collectionName, this.currentVersion);
    }

    /**
     * Migrate a document from its current version to a specific target version
     */
    async migrateDocumentToVersion(
        data: any,
        documentId: string,
        collectionName: string,
        targetVersion: number
    ): Promise<MigrationResult<T>> {
        const docVersion = data.schemaVersion || 0;

        //console.log(`Checking migration for ${collectionName}/${documentId} from version ${docVersion} to ${targetVersion}`);

        if (docVersion === targetVersion) {
            return {
                data: data as T,
                version: docVersion,
                migrated: false
            };
        }

        if (docVersion > targetVersion) {
            console.warn(
                `Document ${documentId} in ${collectionName} has version ${docVersion} ` +
                `which is higher than target version ${targetVersion}. ` +
                `This may indicate a rollback or version mismatch.`
            );
            return {
                data: data as T,
                version: docVersion,
                migrated: false
            };
        }

        let migratedData = { ...data };
        let lastVersion = docVersion;

        // Apply migrations in order
        for (const migration of this.migrations) {
            if (migration.version > docVersion && migration.version <= targetVersion) {
                const context: MigrationContext = {
                    documentId,
                    collectionName,
                    currentVersion: lastVersion,
                    targetVersion: migration.version
                };

                try {
                    console.log(
                        `Migrating ${documentId} in ${collectionName} from version ${lastVersion} to ${migration.version}: ${migration.description}`
                    );

                    migratedData = await migration.migrate(migratedData, context);
                    lastVersion = migration.version;
                } catch (error) {
                    console.error(
                        `Migration failed for ${documentId} in ${collectionName} from version ${lastVersion} to ${migration.version}:`,
                        error
                    );
                    throw error;
                }
            }
        }

        // Update the schema version
        migratedData.schemaVersion = targetVersion;

        return {
            data: migratedData as T,
            version: targetVersion,
            migrated: true
        };
    }

    /**
     * Get the current schema version
     */
    getCurrentVersion(): number {
        return this.currentVersion;
    }

    /**
     * Get all registered migrations
     */
    getMigrations(): MigrationDefinition<T>[] {
        return [...this.migrations];
    }
}

/**
 * Helper function to safely access nested properties with fallback
 */
export function safeGet<T>(obj: any, path: string, defaultValue: T): T {
    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
        if (current && typeof current === 'object' && key in current) {
            current = current[key];
        } else {
            return defaultValue;
        }
    }

    return current !== undefined ? current : defaultValue;
}

/**
 * Helper function to safely set nested properties
 */
export function safeSet(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current) || typeof current[key] !== 'object') {
            current[key] = {};
        }
        current = current[key];
    }

    current[keys[keys.length - 1]] = value;
}

/**
 * Helper function to remove properties from an object
 */
export function removeProperties(obj: any, properties: string[]): void {
    for (const prop of properties) {
        delete obj[prop];
    }
}

/**
 * Helper function to rename properties in an object
 */
export function renameProperty(obj: any, oldName: string, newName: string): void {
    if (oldName in obj) {
        obj[newName] = obj[oldName];
        delete obj[oldName];
    }
} 